import type { MakerAgent, SharingClosedReason } from '@ralph/contracts'
import type { I18n, Messages } from '@/i18n/index.ts'

/**
 * The agent's sharing request, as its lifecycle bar shows it (PRD-26 GRP-081, D-51, D-52).
 *
 * Waiting shows "Waiting for review" and a way to withdraw it — any current maintainer may, not
 * only whoever sent it. An outcome — approved, declined, closed — stays until the person reading
 * it dismisses it, per reader, because the maker who sent it on Monday and the co-maintainer who
 * opens the agent on Tuesday both need to learn what happened. A withdrawn or superseded request
 * says nothing: somebody chose that, or a newer request is the one to read.
 */

type Request = NonNullable<MakerAgent['sharingRequest']>

export type SharingBanner =
  | { kind: 'waiting'; id: string; whom: string; at: string; by: string | null }
  | { kind: 'approved' | 'declined'; id: string; whom: string; at: string; by: string | null; note: string }
  | { kind: 'closed'; id: string; whom: string; reason: SharingClosedReason | null }

/**
 * The catalogue this module reads from, handed in rather than imported.
 *
 * `everyone who signs in` is one of D-37's bound phrases and belongs to the publish namespace;
 * reaching for it at module scope — `makerStrings.lifecycle.request.everyone`, which is what
 * stood here — would pin the language the bundle happened to evaluate in (PRD-27 F-3).
 */
type PublishWords = Messages['marketplace']['publish']

/** "Legal and Ops", or "everyone who signs in" — joined by `Intl.ListFormat`, which is why the
 * locale comes in rather than an " and " going out (PRD-27 D-19). */
export const requestWhom = (
  t: PublishWords,
  fmt: Pick<I18n, 'formatList'>,
  request: Pick<Request, 'requestedKind' | 'groups'>
): string =>
  request.requestedKind === 'everyone'
    ? t.lifecycle.request.everyone
    : fmt.formatList(request.groups.map((group) => group.name))

export const sharingBanner = (
  t: PublishWords,
  fmt: Pick<I18n, 'formatList'>,
  request: Request | null,
  dismissed: ReadonlySet<string>
): SharingBanner | null => {
  if (request === null) return null
  const whom = requestWhom(t, fmt, request)

  if (request.status === 'pending') {
    return { kind: 'waiting', id: request.id, whom, at: request.requestedAt, by: request.requestedByName }
  }
  if (dismissed.has(request.id)) return null

  if (request.status === 'approved' || request.status === 'declined') {
    return {
      kind: request.status,
      id: request.id,
      whom,
      at: request.decidedAt ?? request.requestedAt,
      by: request.decidedByName,
      note: request.decisionNote
    }
  }
  if (request.status === 'closed') return { kind: 'closed', id: request.id, whom, reason: request.closedReason }
  return null
}

// ---------------------------------------------------------------------------
// Dismissed outcomes, per reader
// ---------------------------------------------------------------------------

/**
 * In this browser's storage, because a dismissal is a reader's convenience rather than a fact about
 * the agent: it survives a reload and reaches nobody else. Every access is guarded — a private
 * window or blocked site data makes `localStorage` throw, and the bar must still render (it simply
 * shows the outcome again next time). Capped, so it cannot grow for ever.
 */
const KEY = 'ralph.sharing-outcomes.dismissed'
const CAP = 100

export const readDismissedOutcomes = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [])
  } catch {
    return new Set()
  }
}

export const rememberDismissedOutcome = (id: string): Set<string> => {
  const next = readDismissedOutcomes()
  next.add(id)
  try {
    window.localStorage.setItem(KEY, JSON.stringify([...next].slice(-CAP)))
  } catch {
    // Storage is unavailable; the dismissal holds for this page view only.
  }
  return next
}
